/* ── Black Friday tiers (one-time purchases only) ──
   qty = bags, pct = discount on all bags (must match the automatic discount in Shopify),
   free = bags shown as "gratis" in the copy (3 + 1 gratis). Display only. */
const BF_TIERS = [
  { qty: 1, pct: 10, free: 0 },
  { qty: 2, pct: 20, free: 0 },
  { qty: 4, pct: 25, free: 1 },
  { qty: 6, pct: 33, free: 2 }
];
/* Cart upsell: current qty → offered qty */
const BF_UPSELL = { 1: 2, 4: 6 };
const MAX_QTY = 6;

/* Strings come from locales/*.json (black_friday.*) via layout/theme.liquid */
const tr = (key, vars = {}) =>
  (window.__bf?.strings?.[key] || key).replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? '');
const bagLabel = n => n === 1 ? tr('bag_one') : tr('bag_other', { count: n });

const bf = {
  get enabled() { return !!window.__bf?.enabled; },
  appliesTo(productId) { return this.enabled && productId === window.__bf.productId; },
  tier(qty) { return BF_TIERS.find(t => t.qty === qty) || null; },
  /* Largest tier ≤ qty, used when switching from subscription back to one-time */
  snap(qty) { return [...BF_TIERS].reverse().find(t => t.qty <= qty) || BF_TIERS[0]; },
  /* Tier total in cents */
  price(tier, unitCents) { return Math.round(unitCents * tier.qty * (100 - tier.pct) / 100); },
  free(tier) { return tier.free; },
  pay(tier) { return tier.qty - tier.free; },
  title(tier) { return tier.free ? tr('deal', { pay: this.pay(tier), free: tier.free }) : bagLabel(tier.qty); },
  badge(tier) { return tier.free ? tr('deal_badge', { count: tier.qty, pay: this.pay(tier) }) : tr('pct_off', { pct: tier.pct }); },
  priceBadge(tier) { return this.free(tier) ? this.title(tier) : tr('price_badge', { pct: tier.pct }); },
  summary(tier) { return tr('summary', { bags: bagLabel(tier.qty), deal: this.free(tier) ? this.title(tier) : tr('pct_off', { pct: tier.pct }) }); },
  explain(tier) {
    return this.free(tier)
      ? tr('explain_deal', { count: tier.qty, pay: this.pay(tier) })
      : tr('explain_pct', { bags: bagLabel(tier.qty), pct: tier.pct });
  }
};
window.bf = bf;

document.addEventListener('alpine:init', () => {

  /* €16,61 — accepts euros as number or string */
  Alpine.magic('money', () => euros => '€' + (parseFloat(euros) || 0).toFixed(2).replace('.', ','));

  /* ── Body scroll lock (iOS Safari ignores overflow:hidden on body) ── */
  let _lockedScrollY = 0;
  function lockBodyScroll() {
    if (document.body.style.position === 'fixed') return;
    _lockedScrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${_lockedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
  }
  function unlockBodyScroll() {
    if (document.body.style.position !== 'fixed') return;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    window.scrollTo(0, _lockedScrollY);
  }

  /* ── Product ── */
  Alpine.store('product', {
    product: null,
    variants: [],
    selectedVariant: null,
    quantity: 1,
    sellingPlanId: null,
    loading: false,

    init() {
      if (window.__product) {
        this.product = window.__product;
        this.variants = window.__productVariants || [];
        this.selectedVariant = this.variants.find(v => v.available) || this.variants[0];
      }
    },

    get currentVariant() {
      return this.selectedVariant;
    },

    get available() {
      return this.selectedVariant?.available ?? false;
    },

    get price() {
      if (this.selectedVariant && this.sellingPlanId) {
        const alloc = this.selectedVariant.selling_plan_allocations?.find(
          a => a.selling_plan_id === this.sellingPlanId
        );
        if (alloc) return (alloc.price / 100).toFixed(2);
      }
      return this.selectedVariant
        ? (this.selectedVariant.price / 100).toFixed(2)
        : '';
    },

    get compareAtPrice() {
      if (this.selectedVariant && this.sellingPlanId) {
        const alloc = this.selectedVariant.selling_plan_allocations?.find(
          a => a.selling_plan_id === this.sellingPlanId
        );
        if (alloc && alloc.compare_at_price && alloc.compare_at_price !== alloc.price) {
          return (alloc.compare_at_price / 100).toFixed(2);
        }
      }
      if (!this.selectedVariant?.compare_at_price) return '';
      return (this.selectedVariant.compare_at_price / 100).toFixed(2);
    },

    /* ── Black Friday ── */
    get bfActive() {
      return !this.sellingPlanId && bf.appliesTo(this.product?.id);
    },

    get bfTier() {
      return this.bfActive ? bf.tier(this.quantity) : null;
    },

    get subscriptionAllocation() {
      return this.selectedVariant?.selling_plan_allocations?.[0] || null;
    },

    get subscriptionPct() {
      const alloc = this.subscriptionAllocation;
      if (!alloc || !this.selectedVariant) return 0;
      return Math.round((1 - alloc.price / this.selectedVariant.price) * 100);
    },

    /* Dropdown options: BF tiers for one-time, 1–MAX_QTY otherwise */
    get quantityOptions() {
      const base = this.selectedVariant?.price || 0;
      if (this.bfActive) {
        return BF_TIERS.map(t => ({
          qty: t.qty,
          title: bf.title(t),
          badge: bf.badge(t),
          compare: (base * t.qty / 100).toFixed(2),
          price: (bf.price(t, base) / 100).toFixed(2)
        }));
      }
      const alloc = this.sellingPlanId ? this.subscriptionAllocation : null;
      return Array.from({ length: MAX_QTY }, (_, i) => {
        const qty = i + 1;
        return {
          qty,
          title: bagLabel(qty),
          badge: alloc ? tr('pct_off', { pct: this.subscriptionPct }) : '',
          compare: alloc ? (base * qty / 100).toFixed(2) : '',
          price: ((alloc ? alloc.price : base) * qty / 100).toFixed(2)
        };
      });
    },

    get quantitySummary() {
      if (this.bfTier) return bf.summary(this.bfTier);
      if (this.sellingPlanId) return tr('summary', { bags: bagLabel(this.quantity), deal: tr('pct_off', { pct: this.subscriptionPct }) });
      return bagLabel(this.quantity);
    },

    get quantityExplain() {
      if (this.bfTier) return bf.explain(this.bfTier);
      if (this.sellingPlanId) return tr('explain_subscription', { bags: bagLabel(this.quantity), pct: this.subscriptionPct });
      return '';
    },

    get badgeText() {
      return this.bfTier ? bf.priceBadge(this.bfTier) : '';
    },

    setPurchaseType(type) {
      if (type === 'subscription') {
        const alloc = this.subscriptionAllocation;
        if (alloc) this.sellingPlanId = alloc.selling_plan_id;
        return;
      }
      this.sellingPlanId = null;
      if (this.bfActive && !bf.tier(this.quantity)) this.quantity = bf.snap(this.quantity).qty;
    },

    /* Total = unit price × quantity (or the BF tier price) */
    get totalPrice() {
      if (this.bfTier) return (bf.price(this.bfTier, this.selectedVariant.price) / 100).toFixed(2);
      const unit = parseFloat(this.price);
      if (isNaN(unit)) return '';
      return (unit * this.quantity).toFixed(2);
    },

    get totalCompareAtPrice() {
      if (this.bfTier) return (this.selectedVariant.price * this.quantity / 100).toFixed(2);
      const unit = parseFloat(this.compareAtPrice);
      if (isNaN(unit) || !this.compareAtPrice) return '';
      return (unit * this.quantity).toFixed(2);
    },

    selectVariant(variantId) {
      const v = this.variants.find(v => v.id === variantId);
      if (v) {
        this.selectedVariant = v;
        /* Reset selling plan if new variant doesn't have it */
        if (this.sellingPlanId) {
          const hasAlloc = v.selling_plan_allocations?.some(
            a => a.selling_plan_id === this.sellingPlanId
          );
          if (!hasAlloc) this.sellingPlanId = null;
        }
      }
    },

    async addToCart() {
      if (!this.selectedVariant || this.loading) return;
      this.loading = true;

      const payload = {
        id: this.selectedVariant.id,
        quantity: this.quantity
      };

      if (this.sellingPlanId) {
        payload.selling_plan = this.sellingPlanId;
      }

      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        Alpine.store('cart').open();
        await Alpine.store('cart').refresh();
      } catch (e) {
        console.error('Add to cart failed', e);
      } finally {
        this.loading = false;
      }
    }
  });


  /* ── Product Card Store ── */
  Alpine.store('productCard', {
    loading: null,

    async addToCart(variantId) {
      if (this.loading) return;
      this.loading = variantId;

      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });

        Alpine.store('cart').open();
        await Alpine.store('cart').refresh();
      } catch (e) {
        console.error('Quick add failed', e);
      } finally {
        this.loading = null;
      }
    }
  });

  /* ── Cart Drawer Store ── */
  Alpine.store('cart', {
    isOpen: false,
    items: [],
    itemCount: 0,
    totalPrice: '0.00',
    loading: false,

    get hasSubscription() {
      return this.items.some(item => !!item.selling_plan_allocation);
    },

    /* ── Black Friday ── */
    upsellLoading: false,
    upsellDismissed: (() => {
      try { return JSON.parse(sessionStorage.getItem('bf-upsell-dismissed')) || []; } catch (e) { return []; }
    })(),

    isBfItem(item) {
      return !item.selling_plan_allocation && bf.appliesTo(item.product_id);
    },

    /* "2 zakken · <title of the automatic discount Shopify actually applied>" */
    bfLabel(item) {
      if (!this.isBfItem(item)) return '';
      const discount = item.line_level_discount_allocations?.[0]?.discount_application?.title;
      return discount ? tr('summary', { bags: bagLabel(item.quantity), deal: discount }) : bagLabel(item.quantity);
    },

    /* BF one-time lines step between tiers (1 → 2 → 4 → 6), others ±1 up to MAX_QTY */
    stepQuantity(item, dir) {
      let qty = item.quantity + dir;
      if (this.isBfItem(item)) {
        const tiers = BF_TIERS.map(t => t.qty);
        qty = dir > 0
          ? tiers.find(q => q > item.quantity) ?? item.quantity
          : [...tiers].reverse().find(q => q < item.quantity) ?? 0;
      }
      if (qty > MAX_QTY) return;
      this.updateQuantity(item.key, qty);
    },

    canIncrease(item) {
      return item.quantity < MAX_QTY;
    },

    /* Only one upsell at a time: the first eligible one-time BF line */
    get upsell() {
      const item = this.items.find(i => this.isBfItem(i) && BF_UPSELL[i.quantity]);
      if (!item || this.upsellDismissed.includes(item.quantity)) return null;

      const from = bf.tier(item.quantity);
      const to = bf.tier(BF_UPSELL[item.quantity]);
      const unit = item.original_price ?? item.price;
      const total = bf.price(to, unit) / 100;
      const extra = (bf.price(to, unit) - bf.price(from, unit)) / 100;
      const money = euros => '€' + euros.toFixed(2).replace('.', ',');
      const free = bf.free(to);

      return {
        key: item.key,
        qty: to.qty,
        title: free
          ? tr('upsell_title_deal', { count: to.qty - from.qty, extra: money(extra) })
          : tr('upsell_title_pct', { bags: bagLabel(to.qty), pct: to.pct }),
        compare: money(unit * to.qty / 100),
        line: free
          ? tr('upsell_line_deal', { bags: bagLabel(to.qty), total: money(total), deal: bf.title(to) })
          : tr('upsell_line_pct', { total: money(total), extra: money(extra) }),
        accept: tr('upsell_accept', { bags: bagLabel(to.qty) }),
        decline: tr('upsell_decline', { bags: bagLabel(from.qty) }),
        fromQty: from.qty
      };
    },

    async acceptUpsell() {
      const offer = this.upsell;
      if (!offer || this.upsellLoading) return;
      this.upsellLoading = true;
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: offer.key, quantity: offer.qty })
        });
        await this.refresh();
      } catch (e) {
        console.error('Upsell failed', e);
      } finally {
        this.upsellLoading = false;
      }
    },

    declineUpsell() {
      const offer = this.upsell;
      if (!offer) return;
      this.upsellDismissed.push(offer.fromQty);
      try { sessionStorage.setItem('bf-upsell-dismissed', JSON.stringify(this.upsellDismissed)); } catch (e) {}
    },

    init() {
      this.refresh();
    },

    toggle() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.refresh();
        lockBodyScroll();
      } else {
        unlockBodyScroll();
      }
    },

    open() {
      this.isOpen = true;
      this.refresh();
      lockBodyScroll();
    },
    close() {
      this.isOpen = false;
      unlockBodyScroll();
    },

    // product_id → custom.cart_image URL (rendered by sections/cart-image-map.liquid)
    imageMap: {},

    applyCartImages(items) {
      items.forEach(item => {
        const img = this.imageMap[item.product_id];
        if (img) item.image = img;
      });
    },

    async fetchImageMap() {
      try {
        const res = await fetch('/?sections=cart-image-map');
        const data = await res.json();
        const doc = new DOMParser().parseFromString(data['cart-image-map'] || '', 'text/html');
        const script = doc.querySelector('[data-cart-image-map]');
        this.imageMap = script ? JSON.parse(script.textContent) : {};
      } catch (e) { /* keep default Shopify images */ }
    },

    async refresh() {
      try {
        const [res] = await Promise.all([fetch('/cart.js'), this.fetchImageMap()]);
        const cart = await res.json();
        this.applyCartImages(cart.items);
        this.items = cart.items;
        this.itemCount = cart.item_count;
        this.totalPrice = (cart.total_price / 100).toFixed(2);

        // Enrich items with variant compare_at_price and selling plan discounts
        const handles = [...new Set(this.items.map(i => i.handle))];
        await Promise.all(handles.map(async (handle) => {
          try {
            const pRes = await fetch(`/products/${handle}.js`);
            const product = await pRes.json();
            this.items.forEach(item => {
              if (item.handle === handle) {
                const variant = product.variants.find(v => v.id === item.variant_id);
                if (variant) {
                  // Compare-at price (sale items)
                  if (variant.compare_at_price && variant.compare_at_price > item.final_price) {
                    item._compareAtLinePrice = variant.compare_at_price * item.quantity;
                  }
                  // Selling plan discount: variant regular price vs subscription price
                  if (item.selling_plan_allocation && variant.price > item.final_price) {
                    item._compareAtLinePrice = variant.price * item.quantity;
                  }
                }
              }
            });
          } catch (e) { /* skip enrichment on error */ }
        }));
      } catch (e) {
        console.error('Cart refresh failed', e);
      }
    },

    async updateQuantity(key, quantity) {
      // Optimistic: update local item immediately (no flicker)
      const index = this.items.findIndex(i => i.key === key);
      if (index === -1) return;

      if (quantity <= 0) {
        // Remove locally right away
        this.items.splice(index, 1);
        this.itemCount = this.items.reduce((s, i) => s + i.quantity, 0);
      } else {
        const item = this.items[index];
        const unitPrice = item.final_line_price / item.quantity;
        const tier = this.isBfItem(item) && bf.tier(quantity);
        item.quantity = quantity;
        item.final_line_price = tier
          ? bf.price(tier, item.original_price ?? item.price)
          : Math.round(unitPrice * quantity);
        this.itemCount = this.items.reduce((s, i) => s + i.quantity, 0);
        this.totalPrice = (this.items.reduce((s, i) => s + i.final_line_price, 0) / 100).toFixed(2);
      }

      // Sync with server
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });
        const cart = await res.json();

        // Reconcile totals from server (authoritative)
        this.itemCount = cart.item_count;
        this.totalPrice = (cart.total_price / 100).toFixed(2);

        // Patch existing items in-place instead of replacing the array
        const serverItems = cart.items;
        this.applyCartImages(serverItems);
        // Remove items no longer in server response
        for (let i = this.items.length - 1; i >= 0; i--) {
          if (!serverItems.find(s => s.key === this.items[i].key)) {
            this.items.splice(i, 1);
          }
        }
        // Update existing & add new
        serverItems.forEach(si => {
          const local = this.items.find(l => l.key === si.key);
          if (local) {
            Object.assign(local, si);
          } else {
            this.items.push(si);
          }
        });
      } catch (e) {
        console.error('Update quantity failed', e);
        // Rollback on error
        await this.refresh();
      }
    },

    async removeItem(key) {
      await this.updateQuantity(key, 0);
    }
  });


  Alpine.store('mobileMenu', {
    isOpen: false,

    toggle() {
      this.isOpen = !this.isOpen;

      if (this.isOpen) {
        lockBodyScroll();
      } else {
        unlockBodyScroll();
      }
    },

    open() {
      this.isOpen = true;
      lockBodyScroll();
    },
    close() {
      this.isOpen = false;
      unlockBodyScroll();
    }
  })
});
